
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useLead } from "@/hooks/useLeads";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { LeadInfo } from "./lead-detail/LeadInfo";
import { LeadContacts } from "./lead-detail/LeadContacts";
import { LeadActivities } from "./lead-detail/LeadActivities";
import { LeadAttachments } from "./lead-detail/LeadAttachments";
import { NewsAlertsTab } from "./lead-detail/NewsAlertsTab";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { LoadingState } from '@/components/error-handling/LoadingState';
import { ErrorState } from "@/components/error-handling/ErrorState";
import { LeadHeader } from "./lead-detail/LeadHeader";
import { LeadForm } from "./LeadForm";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export const LeadDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [showEditForm, setShowEditForm] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [activeNewsTab, setActiveNewsTab] = useState(false);
  
  const { data: lead, isLoading, error, refetch } = useLead(id);
  
  const handleDelete = async () => {
    setDeleteDialogOpen(true);
  };
  
  const confirmDelete = async () => {
    try {
      setIsDeleting(true);
      
      // Delete the lead - cascading will handle related records
      const { error } = await supabase
        .from("lead_master")
        .delete()
        .eq("id", id);
        
      if (error) throw error;
      
      toast.success("Lead deleted successfully");
      navigate("/leads", { replace: true });
      
    } catch (error) {
      console.error("Error deleting lead:", error);
      toast.error("Failed to delete lead. Please try again.");
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
    }
  };
  
  if (isLoading) {
    return <LoadingState />;
  }
  
  if (error) {
    return (
      <ErrorState 
        title="Error loading lead"
        description={error instanceof Error ? error.message : "Failed to load lead details"}
        onRetry={() => refetch()}
      />
    );
  }
  
  if (!lead) {
    return <ErrorState title="Lead not found" description="The requested lead could not be found." />;
  }

  return (
    <div className="space-y-6">
      <LeadHeader 
        lead={lead}
        onEdit={() => setShowEditForm(!showEditForm)}
        onDelete={handleDelete}
      />
      
      {showEditForm ? (
        <LeadForm 
          initialData={lead} 
          onClose={() => setShowEditForm(false)}
          onSuccess={() => {
            setShowEditForm(false);
            refetch(); // refetch is still needed here after full form edit
          }}
        />
      ) : (
        <LeadInfo lead={lead} />
      )}
      
      <Tabs defaultValue="contacts" className="w-full">
        <TabsList className="grid grid-cols-4 md:w-[500px]">
          <TabsTrigger value="contacts">Contacts</TabsTrigger>
          <TabsTrigger value="activities">Activities</TabsTrigger>
          <TabsTrigger value="attachments">Attachments</TabsTrigger>
          <TabsTrigger value="news">News Intelligence</TabsTrigger>
        </TabsList>
        
        <TabsContent value="contacts" className="mt-4">
          <LeadContacts leadId={id!} contacts={lead.contacts} onUpdate={refetch} />
        </TabsContent>
        
        <TabsContent value="activities" className="mt-4">
          <LeadActivities leadId={id!} activities={lead.activities} onUpdate={refetch} />
        </TabsContent>
        
        <TabsContent value="attachments" className="mt-4">
          <LeadAttachments leadId={id!} attachments={lead.attachments} onUpdate={refetch} />
        </TabsContent>
        
        <TabsContent value="news" className="mt-4">
          <NewsAlertsTab 
            leadId={id!} 
            newsAlerts={lead.news_alerts || []} 
            newsMonitoringEnabled={lead.news_monitoring_enabled}
            onUpdate={refetch} 
          />
        </TabsContent>
      </Tabs>
      
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the lead "{lead.name}" and all associated data including contacts, activities, attachments, and history.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDelete} 
              disabled={isDeleting}
              className={isDeleting ? "opacity-70 cursor-not-allowed" : ""}
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default LeadDetail;

